import {
  BookOpen,
  Calculator,
  Compass,
  Globe,
  Lightbulb,
  Palette,
  Play,
} from 'lucide-react';
import type { RecordedClass } from '@/types/recording';

export function getSubjectCategory(subjectName?: string): { label: string; badge: string } {
  const name = subjectName?.toLowerCase() || '';
  if (name.includes('telugu') || name.includes('english') || name.includes('hindi')) {
    return { label: 'Language', badge: 'bg-purple-500/15 text-purple-400 border border-purple-500/25' };
  }
  if (name.includes('math')) {
    return { label: 'Maths', badge: 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/25' };
  }
  if (name.includes('evs') || name.includes('science') || name.includes('environ')) {
    return { label: 'Science', badge: 'bg-blue-500/15 text-blue-400 border border-blue-500/25' };
  }
  if (name.includes('art') || name.includes('craft')) {
    return { label: 'Activity', badge: 'bg-teal-500/15 text-teal-400 border border-teal-500/25' };
  }
  if (name.includes('social')) {
    return { label: 'Social', badge: 'bg-rose-500/15 text-rose-400 border border-rose-500/25' };
  }
  return { label: 'Academic', badge: 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/25' };
}

interface SubjectThumbnailCardProps {
  recording: RecordedClass;
  onClick?: () => void;
}

export function SubjectThumbnailCard({ recording, onClick }: SubjectThumbnailCardProps) {
  const subjectName = recording.subject?.name?.toLowerCase() || '';

  // Artwork only, keyed loosely on the subject's name. The words on the card are
  // the subject as the school named it: this used to print its own titles, so
  // any "science" read "EVS · Our World" and an unmatched one wore "Language".
  let config = {
    bg: 'bg-gradient-to-br from-indigo-900/90 via-purple-900/80 to-slate-950',
    border: 'border-purple-500/30',
    icon: BookOpen,
  };

  if (subjectName.includes('telugu')) {
    config = {
      bg: 'bg-gradient-to-br from-purple-900/90 via-indigo-950 to-slate-950',
      border: 'border-purple-500/30',
      icon: BookOpen,
    };
  } else if (subjectName.includes('math')) {
    config = {
      bg: 'bg-gradient-to-br from-emerald-900/90 via-teal-950 to-slate-950',
      border: 'border-emerald-500/30',
      icon: Calculator,
    };
  } else if (subjectName.includes('evs') || subjectName.includes('science') || subjectName.includes('environ')) {
    config = {
      bg: 'bg-gradient-to-br from-blue-900/90 via-sky-950 to-slate-950',
      border: 'border-blue-500/30',
      icon: Globe,
    };
  } else if (subjectName.includes('english')) {
    config = {
      bg: 'bg-gradient-to-br from-amber-900/90 via-orange-950 to-slate-950',
      border: 'border-amber-500/30',
      icon: BookOpen,
    };
  } else if (subjectName.includes('gk') || subjectName.includes('general knowledge')) {
    config = {
      bg: 'bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950',
      border: 'border-indigo-500/30',
      icon: Lightbulb,
    };
  } else if (subjectName.includes('art') || subjectName.includes('craft')) {
    config = {
      bg: 'bg-gradient-to-br from-teal-900/90 via-cyan-950 to-slate-950',
      border: 'border-teal-500/30',
      icon: Palette,
    };
  } else if (subjectName.includes('social')) {
    config = {
      bg: 'bg-gradient-to-br from-rose-900/90 via-pink-950 to-slate-950',
      border: 'border-rose-500/30',
      icon: Compass,
    };
  }

  const IconComp = config.icon;

  return (
    <div
      onClick={onClick}
      className={`group relative flex h-20 w-36 sm:w-44 shrink-0 cursor-pointer items-center justify-between overflow-hidden rounded-xl border p-2.5 shadow-md transition-all hover:scale-[1.02] hover:shadow-lg ${config.bg} ${config.border}`}
    >
      {/* Background Decorative Pattern / Glow */}
      <div className="pointer-events-none absolute -right-4 -bottom-4 h-16 w-16 rounded-full bg-white/5 blur-xl group-hover:bg-white/10 transition-colors" />

      {/* Left: Play button circular overlay */}
      <div className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-xs border border-white/20 shadow-xs group-hover:bg-indigo-600 group-hover:border-indigo-400 transition-all">
        <Play className="h-3.5 w-3.5 fill-white ml-0.5" />
      </div>

      {/* Right: the subject's real name, with its icon */}
      <div className="relative z-10 flex flex-col items-end justify-center text-right min-w-0 pl-2">
        <span className="truncate max-w-full text-xs font-black text-white leading-tight">
          {recording.subject?.name || '—'}
        </span>
        <IconComp className="mt-1 h-2.5 w-2.5 text-slate-300" aria-hidden />
      </div>
    </div>
  );
}
